import { useEffect, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  Crown, Anchor, Heart, Shield, Sparkles, LogOut, ChevronRight, Megaphone,
  TrendingUp, Award, Star, Compass, Gem,
} from "lucide-react";

/**
 * Ashairah Flagship — heir-only dashboard.
 * Role gate: heir | guardian | admin (oversight tiers can also view).
 */
export default function AshairahFlagship() {
  const { user, loading, logout } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const refresh = useCallback(() => {
    api.get("/heir/flagship")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Flagship unreachable"));
  }, []);

  useEffect(() => {
    if (user && ["heir", "guardian", "admin"].includes(user.role)) refresh();
  }, [user, refresh]);

  useEffect(() => {
    const prev = document.title;
    document.title = "Ashairah Flagship | EIGOO Armada";
    return () => { document.title = prev; };
  }, []);

  if (loading) return <Layout><div className="px-6 py-32 text-white/40">Authorising…</div></Layout>;
  if (!user || !["heir", "guardian", "admin"].includes(user.role)) {
    return <Navigate to="/admiral/login" replace />;
  }
  if (err) return <Layout><div className="px-6 py-32 text-rose-300/80" data-testid="flagship-error">{err}</div></Layout>;
  if (!data) return <Layout><div className="px-6 py-32 text-white/40">Loading the Flagship…</div></Layout>;

  const moneyFmt = (v) => `$${(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const pct = (v) => `${Math.round(v * 100)}%`;

  return (
    <Layout>
      <section className="relative py-12 px-6 md:px-12 lg:px-20" data-testid="ashairah-flagship-page">
        <div className="max-w-6xl mx-auto">

          {/* HEADER */}
          <div className="flex items-center justify-between flex-wrap gap-4 mb-2">
            <div>
              <span className="label-gold">{data.heir.designation}</span>
              <h1
                data-testid="ashairah-flagship-title"
                className="text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tighter text-white mt-3"
              >
                Ashairah <span className="gold-shimmer">Flagship</span> <span className="text-white/40">|</span> EIGOO Armada
              </h1>
              <p
                data-testid="oversight-line"
                className="text-[10px] tracking-[0.3em] uppercase text-[#C9A227] mt-3 font-bold"
              >
                {data.oversight_text}
              </p>
            </div>
            <button onClick={logout} className="btn-outline-gold text-[10px]" data-testid="flagship-logout-btn">
              <LogOut size={12} className="inline -mt-0.5 mr-1"/> Logout
            </button>
          </div>

          {/* WELCOME PANEL */}
          <div className="glass border border-[#C9A227]/40 p-7 md:p-10 mt-8" style={{borderRadius:0}}>
            <div className="grid md:grid-cols-[1fr_auto_1fr] items-center gap-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="text-[#C9A227]" size={18}/>
                  <span className="text-[10px] tracking-[0.3em] uppercase text-[#C9A227] font-bold">Sovereign Heir</span>
                </div>
                <div
                  data-testid="ashairah-name"
                  className="text-3xl md:text-5xl font-black uppercase tracking-tighter gold-shimmer"
                >
                  {data.heir.name}
                </div>
                <div className="text-white/55 text-xs mt-2 truncate">{data.heir.email}</div>
              </div>
              <div className="hidden md:block">
                <div className="w-32 h-px bg-gradient-to-r from-transparent via-[#C9A227] to-transparent"/>
                <div className="text-center text-[#C9A227] text-[10px] tracking-[0.3em] uppercase font-bold mt-2">Lineage Bridge</div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2 justify-end mb-2">
                  <span className="text-[10px] tracking-[0.3em] uppercase text-[#C9A227] font-bold">Guardian</span>
                  <Shield className="text-[#C9A227]" size={18}/>
                </div>
                <div className="text-2xl md:text-3xl font-black uppercase tracking-tighter text-white">{data.guardian.name}</div>
                <div className="text-white/40 text-xs mt-1 truncate">{data.guardian.email}</div>
              </div>
            </div>
          </div>

          {/* HEIR VAULT — HERO */}
          <div className="mt-8 glass border border-[#C9A227]/50 p-7 md:p-10 relative overflow-hidden" style={{borderRadius:0}}>
            <div className="absolute inset-0 pointer-events-none opacity-30"
              style={{background: "radial-gradient(circle at 80% 20%, rgba(212,175,55,0.25), transparent 60%)"}} />
            <div className="relative flex items-center justify-between flex-wrap gap-4 mb-5">
              <h2 className="text-white text-xl md:text-2xl font-black uppercase tracking-tight flex items-center gap-3">
                <Gem className="text-[#C9A227]"/> Heir Vault · {data.heir.name.split(" ")[0]}
              </h2>
              <span
                data-testid="mothers-designation-status"
                className={`text-[10px] tracking-[0.3em] uppercase font-bold px-3 py-1.5 ${
                  data.vault.mothers_designation_active
                    ? "text-[#C9A227] border border-[#C9A227]"
                    : "text-white/50 border border-white/20"
                }`}
              >
                {data.vault.mothers_designation_active
                  ? "Mother's Designation · ACTIVE"
                  : "Mother's Designation · Paused"}
              </span>
            </div>

            <div className="relative grid md:grid-cols-3 gap-6">
              <div className="border border-[#C9A227]/30 p-6">
                <div className="label-gold text-[10px] mb-2">Vault Balance</div>
                <div
                  data-testid="flagship-vault-balance"
                  className="text-5xl md:text-6xl font-black tracking-tighter gold-shimmer"
                >
                  {moneyFmt(data.vault.balance)}
                </div>
                <div className="text-white/40 text-xs mt-2">{data.vault.ledger_count} ledger entries</div>
              </div>
              <div className="border border-white/10 p-6">
                <div className="label-gold text-[10px] mb-2 flex items-center gap-2"><Anchor size={11}/> Pacific Siren · Boarded</div>
                <div className="text-5xl font-black tracking-tighter text-white">
                  {data.platform.boarded_candidates}
                </div>
                <div className="text-white/40 text-xs mt-2">Admiral candidates confirmed</div>
              </div>
              <div className="border border-white/10 p-6">
                <div className="label-gold text-[10px] mb-2 flex items-center gap-2"><Award size={11}/> Armada · Raffle</div>
                <div className="text-5xl font-black tracking-tighter text-white">
                  {data.platform.raffle_tickets_issued}
                </div>
                <div className="text-white/40 text-xs mt-2">Tickets issued to the lineage</div>
              </div>
            </div>
          </div>

          {/* LINEAGE LAW */}
          <div className="mt-8 grid md:grid-cols-3 gap-6">
            <LineageBlock
              testid="lineage-admiral"
              icon={<Crown className="text-[#C9A227]"/>}
              label="Admiral"
              value={pct(data.lineage_law.admiral_pct)}
              caption="Routes to Heir Vault when Mother's Designation is active."
            />
            <LineageBlock
              testid="lineage-lineage"
              icon={<Heart className="text-[#C9A227]"/>}
              label="Lineage"
              value={pct(data.lineage_law.lineage_pct)}
              caption="Reserved for the Pacific Siren bloodline."
            />
            <LineageBlock
              testid="lineage-eigoo"
              icon={<Compass className="text-[#C9A227]"/>}
              label="EIGOO"
              value={pct(data.lineage_law.eigoo_pct)}
              caption="Operating reserve · Flagship infrastructure."
            />
          </div>

          {/* ENDORSED CANDIDATES */}
          <div className="mt-12">
            <div className="flex items-end justify-between mb-5">
              <h2 className="text-white text-xl md:text-2xl font-black uppercase tracking-tight flex items-center gap-3">
                <Star className="text-[#C9A227]"/> Flagship Endorsements
              </h2>
              <Link to="/leaderboard" className="text-[10px] tracking-[0.2em] uppercase text-[#C9A227] font-bold">
                See full leaderboard <ChevronRight size={12} className="inline -mt-0.5"/>
              </Link>
            </div>

            {data.endorsed_candidates.length === 0 ? (
              <div className="glass border border-white/10 p-10 text-center" style={{borderRadius:0}}>
                <Sparkles className="mx-auto text-[#C9A227]" size={28}/>
                <p className="text-white/55 text-sm mt-4 max-w-md mx-auto">
                  The Flagship has not yet selected its first endorsements. Once Admirals are confirmed on the Pacific Siren casting call,
                  Ashairah’s curated lineup will appear here.
                </p>
                <Link to="/leaderboard" className="btn-outline-gold inline-flex mt-6 text-[10px]">
                  View Casting Call
                </Link>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {data.endorsed_candidates.map((c) => (
                  <Link
                    key={c.id}
                    to={`/junior/${c.id}`}
                    data-testid={`endorsed-${c.id}`}
                    className="glass border border-[#C9A227]/20 hover:border-[#C9A227]/60 p-5 group transition-colors"
                    style={{borderRadius:0}}
                  >
                    {c.portfolio_thumb ? (
                      <div className="aspect-[4/5] overflow-hidden mb-4 border border-[#C9A227]/20">
                        <img
                          src={c.portfolio_thumb}
                          alt={c.name}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      </div>
                    ) : (
                      <div className="aspect-[4/5] mb-4 border border-[#C9A227]/20 flex items-center justify-center">
                        <Sparkles className="text-[#C9A227]/40" size={32}/>
                      </div>
                    )}
                    <div className="label-gold text-[10px] mb-1">{c.niche || "Candidate"}</div>
                    <div className="text-white font-black uppercase tracking-tight text-lg">{c.name}</div>
                    <div className="flex items-center gap-1 text-[#C9A227] text-xs mt-2">
                      <TrendingUp size={12}/> {c.upvotes} viral strikes
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* QUICK LINKS */}
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            <QuickLink to="/leaderboard" testid="quick-leaderboard" icon={<Megaphone size={16}/>} label="Casting Call" sub="Public leaderboard"/>
            <QuickLink to="/armada" testid="quick-armada" icon={<Anchor size={16}/>} label="Armada" sub="Raffle & lineage roll"/>
            <QuickLink to="/storefront" testid="quick-storefront" icon={<Gem size={16}/>} label="Glow Storefront" sub="Atelier drops"/>
          </div>

        </div>
      </section>
    </Layout>
  );
}

function LineageBlock({ icon, label, value, caption, testid }) {
  return (
    <div data-testid={testid} className="glass border border-[#C9A227]/20 p-6" style={{borderRadius:0}}>
      <div className="flex items-center gap-2 mb-3">{icon}
        <span className="text-[10px] tracking-[0.3em] uppercase text-[#C9A227] font-bold">{label}</span>
      </div>
      <div className="text-5xl font-black tracking-tighter text-white">{value}</div>
      <p className="text-white/50 text-xs mt-3 leading-relaxed">{caption}</p>
    </div>
  );
}

function QuickLink({ to, icon, label, sub, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="glass border border-white/10 hover:border-[#C9A227]/50 p-6 block transition-colors"
      style={{borderRadius:0}}
    >
      <div className="flex items-center gap-3 text-[#C9A227]">{icon}
        <span className="text-[10px] tracking-[0.3em] uppercase font-bold">{label}</span>
      </div>
      <div className="text-white font-black uppercase tracking-tight text-lg mt-2">{sub}</div>
      <ChevronRight size={14} className="text-[#C9A227] mt-3"/>
    </Link>
  );
}
